import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Moon, Loader2, Sparkles, Gem, Flower } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";

const MoonCycles = () => {
  const [moonData, setMoonData] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchMoonData();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchMoonCalendar();
  }, [selectedMonth, selectedYear]);

  const fetchMoonData = async () => {
    try {
      const res = await axios.get(`${API}/moon/current`);
      setMoonData(res.data);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  const fetchMoonCalendar = async () => {
    try {
      const res = await axios.get(`${API}/moon/calendar?month=${selectedMonth}&year=${selectedYear}`);
      setCalendar(res.data);
    } catch (error) {
      // data loads with fallback state
    }
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="moon-cycles-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Moon className="w-8 h-8 text-moon-gold" />
          Moon Cycles
        </h1>
        <p className="text-mystic-200/70">Track lunar phases and their influence</p>
      </motion.div>

      {/* Current Moon Phase */}
      {moonData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass border-moon-gold/20" data-testid="current-moon-card">
            <CardContent className="py-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Moon Visual */}
                <div className="text-center">
                  <div className="relative inline-block">
                    <span className="text-8xl md:text-9xl" role="img" aria-label={moonData.phase_name}>
                      {moonData.phase_emoji}
                    </span>
                    <div className="absolute inset-0 bg-moon-gold/10 rounded-full blur-3xl"></div>
                  </div>
                  <h2 className="text-3xl font-bold text-white mt-6">{moonData.phase_name}</h2>
                  <p className="text-moon-gold text-lg">{moonData.illumination}% illuminated</p>
                  <p className="text-mystic-200/70 mt-2">Day {moonData.phase_day} of lunar cycle</p>
                </div>

                {/* Moon Details */}
                <div className="space-y-4">
                  <div className="glass-light rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-moon-gold" />
                      <span className="text-white font-medium">Moon Energy</span>
                    </div>
                    <p className="text-mystic-200/80">{moonData.energy}</p>
                  </div>

                  <div className="glass-light rounded-xl p-4">
                    <p className="text-mystic-300 text-sm mb-2">Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {moonData.keywords?.map((keyword, i) => (
                        <span key={`month-${i}`} className="text-xs bg-mystic-700/50 text-mystic-100 px-3 py-1 rounded-full">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="glass-light rounded-xl p-4">
                    <p className="text-mystic-300 text-sm mb-2">Ritual Focus</p>
                    <p className="text-mystic-100">{moonData.ritual_focus}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="glass-light rounded-xl p-3 text-center">
                      <Gem className="w-5 h-5 text-mystic-300 mx-auto mb-1" />
                      <p className="text-xs text-mystic-300">Crystal</p>
                      <p className="text-white text-sm">{moonData.crystal}</p>
                    </div>
                    <div className="glass-light rounded-xl p-3 text-center">
                      <Flower className="w-5 h-5 text-mystic-300 mx-auto mb-1" />
                      <p className="text-xs text-mystic-300">Herb</p>
                      <p className="text-white text-sm">{moonData.herb}</p>
                    </div>
                    <div className="glass-light rounded-xl p-3 text-center">
                      <span className="text-mystic-300">💧</span>
                      <p className="text-xs text-mystic-300">Oil</p>
                      <p className="text-white text-sm">{moonData.oil}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upcoming Phases */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex flex-wrap justify-center gap-8">
                  <div className="text-center">
                    <p className="text-mystic-300 text-sm">Next New Moon</p>
                    <p className="text-white font-medium">{moonData.days_to_new_moon} days</p>
                  </div>
                  <div className="text-center">
                    <p className="text-mystic-300 text-sm">Next Full Moon</p>
                    <p className="text-white font-medium">{moonData.days_to_full_moon} days</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Moon Calendar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="glass" data-testid="moon-calendar-card">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
            <CardTitle className="text-lg text-white">Moon Calendar</CardTitle>
            <div className="flex gap-2">
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-32 bg-mystic-900/50 border-white/10 text-white" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-mystic-800 border-white/10">
                  {months.map((month, i) => (
                    <SelectItem key={`month-${i}`} value={(i + 1).toString()} className="text-white hover:bg-mystic-700">
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-24 bg-mystic-900/50 border-white/10 text-white" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-mystic-800 border-white/10">
                  {[2024, 2025, 2026, 2027].map((year) => (
                    <SelectItem key={year} value={year.toString()} className="text-white hover:bg-mystic-700">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {calendar && (
              <div className="grid grid-cols-7 gap-2">
                {/* Day headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-mystic-300 text-sm py-2">
                    {day}
                  </div>
                ))}
                
                {/* Empty cells for first week */}
                {Array(new Date(selectedYear, selectedMonth - 1, 1).getDay()).fill(null).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square"></div>
                ))}
                
                {/* Days */}
                {calendar.days.map((day) => (
                  <div
                    key={day.day}
                    className="aspect-square glass-light rounded-lg flex flex-col items-center justify-center p-1 hover:bg-white/10 transition-colors cursor-pointer"
                    title={`${day.phase_name} - ${day.illumination}%`}
                  >
                    <span className="text-2xl">{day.phase_emoji}</span>
                    <span className="text-xs text-mystic-200">{day.day}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Moon & Menstrual Connection */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="glass-heavy">
          <CardContent className="py-8">
            <h3 className="text-xl font-semibold text-white mb-4 text-center">
              The Moon-Menstrual Connection
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="glass-light rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🌑</span>
                  <span className="text-white font-medium">New Moon</span>
                </div>
                <p className="text-mystic-200/80 text-sm">
                  Traditionally associated with menstruation. A time for rest, reflection, 
                  and turning inward. Honor your need for solitude.
                </p>
              </div>
              <div className="glass-light rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🌒</span>
                  <span className="text-white font-medium">Waxing Moon</span>
                </div>
                <p className="text-mystic-200/80 text-sm">
                  Aligns with the follicular phase. Energy increases, perfect for 
                  new beginnings, planning, and building momentum.
                </p>
              </div>
              <div className="glass-light rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🌕</span>
                  <span className="text-white font-medium">Full Moon</span>
                </div>
                <p className="text-mystic-200/80 text-sm">
                  Corresponds to ovulation. Peak energy, fertility, and magnetism. 
                  A powerful time for manifestation and celebration.
                </p>
              </div>
              <div className="glass-light rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🌘</span>
                  <span className="text-white font-medium">Waning Moon</span>
                </div>
                <p className="text-mystic-200/80 text-sm">
                  Mirrors the luteal phase. Time for completion, release, and 
                  preparation. Listen to your intuition.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default MoonCycles;
